import { type ReactNode, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronUp, Loader2, MessageSquare } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { OrgTicketTimelineItem } from '../hooks/useOrgTicketTimeline'
import { TimelineMessage } from './TimelineMessage'
import { TimelineOpening } from './TimelineOpening'
import { TimelineRow } from './TimelineRow'
import { TimelineStateChange } from './TimelineStateChange'

interface TicketTimelineProps {
  ticketCreatedAt: string
  description?: string | null
  /** Eventos en orden ASCENDENTE — el hook ya invirtió las páginas. */
  items: OrgTicketTimelineItem[]
  /** `sub` del usuario que mira, para rotular sus mensajes como «Tú». */
  currentUserId?: string
  hasMore: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
  /** Cuadro de comentar: cierra el riel como continuación natural del hilo. */
  composer?: ReactNode
  listRef?: Ref<HTMLOListElement>
}

/**
 * Línea de tiempo del ticket: UNA columna con un riel continuo donde la
 * historia se lee de arriba abajo — la apertura, los comentarios públicos y los
 * cambios de estado, mezclados y en orden.
 *
 * Componente presentacional puro: el orden, la paginación y la deduplicación
 * las resuelve `useOrgTicketTimeline`.
 */
export function TicketTimeline({
  ticketCreatedAt,
  description,
  items,
  currentUserId,
  hasMore,
  loadingOlder,
  onLoadOlder,
  composer,
  listRef,
}: TicketTimelineProps) {
  const { t } = useTranslation()

  return (
    <div>
      <ol ref={listRef} aria-label={t('tickets.detail.timelineLabel')}>
        <TimelineOpening
          createdAt={ticketCreatedAt}
          description={description}
        />

        {/*
          El hueco de historia sin cargar va DEBAJO de la apertura y encima de
          los eventos: la apertura es el origen y siempre se ve, aunque el
          ticket tenga más actividad de la que cabe en una página.
        */}
        {hasMore && (
          <TimelineRow
            marker={
              <span className="grid size-8 place-items-center rounded-full bg-neutral-100 text-ink-muted ring-4 ring-white">
                <ChevronUp className="size-4" />
              </span>
            }
          >
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadOlder}
              disabled={loadingOlder}
            >
              {loadingOlder && <Loader2 className="mr-1.5 size-4 animate-spin" />}
              {t('tickets.detail.loadOlder')}
            </Button>
          </TimelineRow>
        )}

        {items.map((item, index) => {
          // El riel del último evento solo baja si hay cuadro de comentar
          // debajo; si no, la línea termina en él.
          const withRail = index < items.length - 1 || Boolean(composer)

          if (item.kind === 'state_change') {
            // Defensivo: un hito sin destino no dice nada — no se pinta.
            if (!item.toState) return null
            return (
              <TimelineStateChange
                key={item.id}
                fromState={item.fromState}
                toState={item.toState}
                createdAt={item.createdAt}
                withRail={withRail}
              />
            )
          }

          return (
            <TimelineMessage
              key={item.id}
              text={item.text ?? ''}
              createdAt={item.createdAt}
              isOwn={Boolean(currentUserId) && currentUserId === item.actorSub}
              isAutomatic={item.isAutomatic}
              authorName={item.actorName}
              withRail={withRail}
            />
          )
        })}
      </ol>

      {composer && (
        <div className="relative pl-11">
          <span className="absolute left-0 top-0 grid size-8 place-items-center rounded-full bg-neutral-100 text-ink-muted ring-4 ring-white">
            <MessageSquare className="size-4" />
          </span>
          {composer}
        </div>
      )}
    </div>
  )
}
